import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import { Plus, ChefHat, ChevronDown, ChevronUp, Edit, Trash2, CheckSquare } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  xxLargeTextSize,
  tertiaryText,
  baseTextSize,
  borderPrimary,
  rounded,
} from '../../../styling/stylingHelper';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import {
  fetchMepSectionsByRestaurant,
  completeMepSection,
  deleteMepSection,
  MepSection,
} from '../../../services/mep/mepService';
import { getTranslatedText, SupportedLanguage } from '../../../services/recipes/recipesService';
import SaveMepSectionModal from './SaveMepSectionModal';
import { DeleteConfirmationModal } from '../../common/DeleteConfirmationModal';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessMepPage } from '../../../services/accessManagement/accessManagement';

const MepPage = () => {
  const { t, i18n } = useTranslation();
  const lang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;
  const { backendUser, accountSettings } = useAuth();
  const navigate = useNavigate();

  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const restaurantId = selectedRestaurant?.id;

  const [mepSections, setMepSections] = useState<MepSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<MepSection | null>(null);
  const [deletingSection, setDeletingSection] = useState<MepSection | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Check if "All Restaurants" is selected
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  // Check if user has access
  const hasAccess = canAccessMepPage(backendUser, accountSettings);

  // Load MEP sections
  const loadMepSections = useCallback(async () => {
    if (!restaurantId || isAllRestaurants) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const fetchedSections = await fetchMepSectionsByRestaurant(restaurantId);
      setMepSections(fetchedSections);
    } catch (error) {
      console.error('Failed to load MEP sections:', error);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, isAllRestaurants]);

  useEffect(() => {
    loadMepSections();
  }, [loadMepSections]);

  const handleSaveMepSection = async () => {
    await loadMepSections();
    setIsCreateModalOpen(false);
    setEditingSection(null);
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleCompleteMepSection = async (sectionId: string) => {
    const section = mepSections.find((s) => s.id === sectionId);
    if (!section) return;

    // Optimistic update: reset tasks immediately
    const resetTasks = { tasksDate: null, items: [] };
    const updatedSection = {
      ...section,
      mepTasks: resetTasks,
    };

    // Update local state optimistically
    setMepSections((prev) =>
      prev.map((s) => (s.id === sectionId ? updatedSection : s))
    );

    try {
      // Persist to backend
      const savedSection = await completeMepSection(sectionId);

      // Compare task states - only update if there are meaningful differences
      const tasksMatch =
        savedSection.mepTasks.tasksDate === resetTasks.tasksDate &&
        savedSection.mepTasks.items.length === resetTasks.items.length;

      if (!tasksMatch) {
        // There were changes from other users, update with server data
        setMepSections((prev) =>
          prev.map((s) => (s.id === sectionId ? savedSection : s))
        );
      }
      // If tasks match, keep the optimistic update (no rerender needed)
    } catch (error) {
      console.error('Failed to complete MEP section:', error);
      // Revert optimistic update on error
      setMepSections((prev) =>
        prev.map((s) => (s.id === sectionId ? section : s))
      );
    }
  };

  const handleDeleteMepSection = async () => {
    if (!deletingSection) return;

    try {
      await deleteMepSection(deletingSection.id);
      await loadMepSections();
      setDeletingSection(null);
    } catch (error) {
      console.error('Failed to delete MEP section:', error);
    }
  };

  if (!hasAccess) {
    return <Navigate to="/" />;
  }

  if (isAllRestaurants) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <ChefHat className="w-8 h-8 text-green-600 dark:text-green-400" />
            <h1 className="text-3xl font-bold text-text dark:text-text-dark">{t('mep.title')}</h1>
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            {t('mep.pleaseSelectSpecificRestaurant')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <MobilePageHeader title={t('mep.title')} right={<RestaurantSelector />} />
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'hidden lg:block')}>{t('mep.title')}</h1>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className={cn(
              smallTextSize,
              'text-white dark:text-white',
              semiBoldText,
              'bg-darkGreenColor dark:bg-darkGreenColor-dark',
              'rounded-xl',
              'px-3 py-2',
              'hover:bg-darkGreenColor/95 dark:hover:bg-darkGreenColor-dark/95',
              'flex items-center gap-2',
              'min-w-[120px]'
            )}
          >
            <Plus className="w-5 h-5" />
            <span className="inline">{t('mep.addMepSection')}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className={cn(baseTextSize, tertiaryText, 'text-center py-10')}>
          <p>{t('mep.loading')}</p>
        </div>
      ) : mepSections.length === 0 ? (
        <div className={cn(baseTextSize, tertiaryText, 'text-center py-10')}>
          <p className={cn(secondaryText)}>{t('mep.noMepSections')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {mepSections
            .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
            .map((section) => {
              const isExpanded = expandedSections.has(section.id);
              const hasTasks = section.mepTasks.tasksDate !== null && section.mepTasks.items.length > 0;

              return (
                <div
                  key={section.id}
                  className={cn(
                    'border',
                    borderPrimary,
                    rounded,
                    'bg-card dark:bg-card-dark',
                    'transition-all'
                  )}
                >
                  <div className="p-4">
                    <div className="flex max-lg:flex-wrap items-center gap-3">
                      {/* Section header */}
                      <div
                        className="flex-1 flex items-center justify-between cursor-pointer"
                        onClick={() => toggleSection(section.id)}
                      >
                        <div className="flex-1">
                          <h3 className={cn(baseTextSize, semiBoldText, primaryText)}>
                            {getTranslatedText(section.sectionNameTranslations, lang)}
                          </h3>
                          <p className={cn(smallTextSize, secondaryText)}>
                            {section.menuItems.length} {t('mep.menuItems')}
                            {hasTasks && (
                              <span className="ml-2">
                                • {t('mep.hasActiveTasks')}
                              </span>
                            )}
                          </p>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className={cn('w-5 h-5', secondaryText)} />
                        ) : (
                          <ChevronDown className={cn('w-5 h-5', secondaryText)} />
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/mep/${section.id}`);
                          }}
                          className={cn(
                            smallTextSize,
                            'px-3 py-1.5 rounded-lg',
                            'border',
                            borderPrimary,
                            secondaryText,
                            'hover:bg-gray-50 dark:hover:bg-gray-800',
                            'flex items-center gap-1.5'
                          )}
                          title={t('mep.openMepSection')}
                        >
                          <ChefHat className="w-4 h-4" />
                          <span>{t('mep.open')}</span>
                        </button>
                        {hasTasks && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCompleteMepSection(section.id);
                            }}
                            className={cn(
                              smallTextSize,
                              'px-3 py-1.5 rounded-lg',
                              'border',
                              borderPrimary,
                              secondaryText,
                              'hover:bg-gray-50 dark:hover:bg-gray-800',
                              'flex items-center gap-1.5'
                            )}
                            title={t('mep.completeMep')}
                          >
                            <CheckSquare className="w-4 h-4" />
                            <span>{t('mep.complete')}</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSection(section);
                          }}
                          className={cn(
                            'p-2 rounded-lg',
                            'border',
                            borderPrimary,
                            'hover:bg-gray-50 dark:hover:bg-gray-800',
                            'text-gray-600 dark:text-gray-400'
                          )}
                          title={t('mep.editMepSection')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingSection(section);
                          }}
                          className={cn(
                            'p-2 rounded-lg',
                            'border',
                            borderPrimary,
                            'hover:bg-red-50 dark:hover:bg-red-900/20',
                            'text-red-600 dark:text-red-400'
                          )}
                          title={t('mep.deleteMepSection')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Expanded menu items */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
                        {section.menuItems.length === 0 ? (
                          <p className={cn(smallTextSize, secondaryText)}>{t('mep.noMenuItems')}</p>
                        ) : (
                          <div className="space-y-2">
                            {section.menuItems.map((menuItem, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                              >
                                <span className={cn(smallTextSize, primaryText)}>
                                  {menuItem}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {restaurantId && (
        <SaveMepSectionModal
          isOpen={isCreateModalOpen || editingSection !== null}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingSection(null);
          }}
          mode={editingSection ? 'edit' : 'create'}
          restaurantId={restaurantId}
          section={editingSection || undefined}
          onSave={handleSaveMepSection}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deletingSection !== null}
        title={t('mep.confirmDelete')}
        message={t('mep.confirmDeleteMessage', { name: deletingSection ? getTranslatedText(deletingSection.sectionNameTranslations, lang) : '' })}
        onConfirm={handleDeleteMepSection}
        onCancel={() => setDeletingSection(null)}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
      />
    </div>
  );
};

export default MepPage;
